import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Package, Wrench, ArrowLeftRight, Trash2, QrCode, Printer, Download } from 'lucide-react';

const STATUS_STYLES = {
  available: { bg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  in_use: { bg: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  maintenance: { bg: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  calibration: { bg: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  retired: { bg: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' },
};

const CONDITION_STYLES = {
  new: 'text-emerald-600', good: 'text-blue-600', fair: 'text-amber-600',
  poor: 'text-orange-600', needs_repair: 'text-red-600', decommissioned: 'text-gray-400',
};

const CATEGORY_LABELS = {
  investigation_tools: 'Investigation Tools', recorders_readout: 'Recorder Readout',
  underwater_salvage: 'Underwater & Salvage', lab_analysis: 'Lab & Analysis',
  safety_ppe: 'Safety & PPE', communication: 'Communication',
  documentation: 'Documentation', vehicles_vessels: 'Vehicles & Vessels',
  it_computing: 'IT & Computing', other: 'Other',
};

export function EquipmentDetailDialog({ item, open, onClose, canManage, qrPreview, loadingQr, onLoadQr, onDownloadQr, onPrintQr, onEdit, onAssign, onReturn, onDelete }) {
  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[85vh] overflow-y-auto w-[95vw] sm:w-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900 flex-wrap">
            <Package className="w-5 h-5 text-blue-500 shrink-0" />
            {item.name}
            <span className="text-sm font-mono text-gray-400 ml-2">{item.asset_tag}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="flex gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${(STATUS_STYLES[item.status] || STATUS_STYLES.available).bg}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${(STATUS_STYLES[item.status] || STATUS_STYLES.available).dot}`}></span>
              {(item.status || '').replace('_', ' ')}
            </span>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 ${CONDITION_STYLES[item.condition] || 'text-gray-600'}`}>{item.condition}</span>
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">{item.sector === 'both' ? 'General' : item.sector}</span>
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">{CATEGORY_LABELS[item.category] || item.category}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {item.serial_number && <div><span className="text-gray-500">Serial:</span> <span className="text-gray-900 ml-1">{item.serial_number}</span></div>}
            {item.manufacturer && <div><span className="text-gray-500">Make:</span> <span className="text-gray-900 ml-1">{item.manufacturer} {item.model}</span></div>}
            {item.location && <div><span className="text-gray-500">Location:</span> <span className="text-gray-900 ml-1">{item.location}</span></div>}
            {item.storage_location && <div><span className="text-gray-500">Storage:</span> <span className="text-gray-900 ml-1">{item.storage_location}</span></div>}
            {item.calibration_due && <div><span className="text-gray-500">Calibration Due:</span> <span className="text-gray-900 ml-1">{item.calibration_due}</span></div>}
            {item.purchase_date && <div><span className="text-gray-500">Purchased:</span> <span className="text-gray-900 ml-1">{item.purchase_date}</span></div>}
            {item.purchase_cost && <div><span className="text-gray-500">Cost:</span> <span className="text-gray-900 ml-1">{item.purchase_cost}</span></div>}
            {item.assigned_investigation_number && <div><span className="text-gray-500">Assigned:</span> <span className="text-blue-600 ml-1 font-medium">{item.assigned_investigation_number}</span></div>}
            {item.assigned_to && <div><span className="text-gray-500">To:</span> <span className="text-gray-900 ml-1">{item.assigned_to}</span></div>}
          </div>
          {item.specifications && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Specifications</div>
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap border border-gray-100">{item.specifications}</div>
            </div>
          )}
          {item.notes && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Notes</div>
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap border border-gray-100">{item.notes}</div>
            </div>
          )}
          {item.history && item.history.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">History</div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {item.history.slice().reverse().map((h, i) => (
                  <div key={`hist-${h.at || i}`} className="flex items-start gap-2 text-xs bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 shrink-0 capitalize">{h.action}</span>
                    <div className="text-gray-500">
                      <span className="text-gray-700 font-medium">{h.by_name || h.by}</span>
                      {h.notes && <span className="ml-1">- {h.notes}</span>}
                      <div className="text-gray-400 mt-0.5">{h.at ? new Date(h.at).toLocaleString() : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {canManage && (
            <div className="space-y-3 pt-3 border-t border-gray-200">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-2">
                  <div className="flex items-center gap-2">
                    <QrCode className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">Equipment QR Label</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {!qrPreview && (
                      <Button size="sm" variant="outline" className="border-gray-300 text-gray-600 text-xs" data-testid="preview-qr-btn" disabled={loadingQr} onClick={() => onLoadQr(item.id)}>
                        <QrCode className="w-3.5 h-3.5 mr-1" /> {loadingQr ? 'Loading...' : 'Preview QR'}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="border-gray-300 text-gray-600 text-xs" data-testid="download-qr-btn" onClick={() => onDownloadQr(item.id, item.asset_tag)}>
                      <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
                    </Button>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="print-qr-btn" onClick={() => onPrintQr(item.id)}>
                      <Printer className="w-3.5 h-3.5 mr-1" /> Print Label
                    </Button>
                  </div>
                </div>
                {qrPreview && (
                  <div className="flex items-start gap-4">
                    <img src={qrPreview} alt="QR Code" className="w-28 h-28 border border-gray-200 rounded-lg bg-white p-1" />
                    <div className="text-xs text-gray-500 space-y-1 flex-1">
                      <p className="font-medium text-gray-700">QR contains:</p>
                      <p>Asset Tag, Name, Serial Number</p>
                      <p>Category, Sector, Condition</p>
                      <p>Calibration Due, Next Service Date</p>
                      <p>Storage Location, Direct link to details</p>
                      <p className="text-gray-400 mt-2 italic">Scan with any QR reader to view equipment info</p>
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-gray-400 mt-2">Print and attach to equipment for instant identification and tracking</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="border-gray-300 text-gray-700" onClick={() => onEdit(item)}>
                  <Wrench className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                {item.status === 'available' && (
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="assign-equipment-btn" onClick={() => onAssign(item.id)}>
                    <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Assign to Investigation
                  </Button>
                )}
                {item.status === 'in_use' && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="return-equipment-btn" onClick={() => onReturn(item.id)}>
                    <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Return
                  </Button>
                )}
                <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="delete-equipment-btn" onClick={() => onDelete(item.id)}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AssignDialog({ open, onClose, investigations, form, setForm, onAssign }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
        <DialogHeader><DialogTitle className="text-gray-900">Assign to Investigation</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Investigation</label>
            <select data-testid="assign-investigation" value={form.investigation_id}
              onChange={e => setForm({ ...form, investigation_id: e.target.value })}
              className="w-full mt-1 bg-white border border-gray-300 text-gray-700 rounded-md px-3 py-2 text-sm">
              <option value="">Select investigation...</option>
              {investigations.map(inv => (
                <option key={inv.id} value={inv.id}>{inv.investigation_number} - {inv.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Assigned To (person)</label>
            <Input data-testid="assign-to" value={form.assigned_to}
              onChange={e => setForm({ ...form, assigned_to: e.target.value })}
              className="bg-white border-gray-300 text-gray-900 mt-1" placeholder="Investigator name or email" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onClose(false)} className="border-gray-300 text-gray-700">Cancel</Button>
            <Button data-testid="confirm-assign-btn" onClick={onAssign} className="bg-blue-600 hover:bg-blue-700 text-white">Assign</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ReturnDialog({ open, onClose, form, setForm, onReturn }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
        <DialogHeader><DialogTitle className="text-gray-900">Return Equipment</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Condition on Return</label>
            <select value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })}
              className="w-full mt-1 bg-white border border-gray-300 text-gray-700 rounded-md px-3 py-2 text-sm">
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
              <option value="needs_repair">Needs Repair</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Return Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full mt-1 bg-white border border-gray-300 text-gray-700 rounded-md px-3 py-2 text-sm min-h-[60px]"
              placeholder="Any damage or notes..." />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onClose(false)} className="border-gray-300 text-gray-700">Cancel</Button>
            <Button onClick={onReturn} className="bg-emerald-600 hover:bg-emerald-700 text-white">Return Equipment</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
